import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, Settings, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { TrackingLink } from '@/lib/marketing';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/ui/theme-toggle";

interface NavItem {
  label: string;
  href: string;
  icon?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  isActive?: boolean;
  customElement?: React.ReactNode;
  mobileChildren?: { label: string; href: string; icon?: React.ReactNode }[];
}

interface NavbarProps {
  siteName: 'jds' | 'askjds';
  navItems: NavItem[];
  showPromo?: boolean;
  logoSize?: 'sm' | 'md' | 'lg';
  className?: string;
  dashboardText?: string;
  dashboardHref?: string;
}

export function Navbar({
  siteName = 'jds',
  navItems,
  showPromo = false,
  logoSize = 'md',
  className,
  dashboardText = siteName === 'jds' ? 'Dashboard' : 'Chat',
  dashboardHref = siteName === 'jds' ? '/courses' : '/chat',
}: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, signOut } = useAuth();
  
  // Close mobile menu on route change (pathname or hash)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.hash]);
  
  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      window.location.href = '/';
    }
  };
  
  // Determine logo size class
  const logoSizeClass = {
    sm: 'h-8',
    md: 'h-10',
    lg: 'h-12'
  }[logoSize];
  
  return (
    <nav
      className={cn(
        "fixed w-full z-40",
        "glass dark:glass-dark",
        // Push navbar content below the iOS status bar / Dynamic Island on the
        // native build. env(safe-area-inset-top) is 0 on web, so this is a
        // no-op in the browser (web stays pixel-identical).
        "pt-safe-top",
        showPromo && "top-10",
        className
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <TrackingLink href="/" campaign="navbar" content="logo" className="transition-opacity hover:opacity-90">
              <img
                data-twic-src="media:images/logo/jd-simplified-logo.png?v=20260530"
                data-twic-transform="quality=95/output=auto"
                data-twic-intrinsic="1280x512"
                alt="JD Simplified Logo"
                className={`${logoSizeClass} dark:hidden`}
              />
              <img
                data-twic-src="media:images/logo/jd-simplified-logo-dark.png?v=20260530"
                data-twic-transform="quality=95/output=auto"
                alt="JD Simplified Logo"
                className={`${logoSizeClass} hidden dark:block`}
              />
            </TrackingLink>
          </div>
          
          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex md:flex-1 md:justify-center md:items-center">
            <div className="flex items-center space-x-4">
              {navItems.map((item) => {
                const isActiveItem = item.isActive || location.pathname === item.href;
                const isExternal = item.href.startsWith('http');

                // If custom element is provided, render it instead of Link
                if (item.customElement) {
                  return <div key={item.label}>{item.customElement}</div>;
                }

                // External links use <a> tag
                if (isExternal) {
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      className={cn(
                        siteName === 'askjds' ? "nav-link-askjds" : "nav-link",
                        isActiveItem && siteName === 'askjds' && "text-[#F37022] after:w-full",
                        isActiveItem && siteName !== 'askjds' && "text-jdblue after:w-full"
                      )}
                      onClick={item.onClick}
                    >
                      {item.label}
                    </a>
                  );
                }

                // Internal links use React Router Link
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      siteName === 'askjds' ? "nav-link-askjds" : "nav-link",
                      isActiveItem && siteName === 'askjds' && "text-[#F37022] after:w-full",
                      isActiveItem && siteName !== 'askjds' && "text-jdblue after:w-full"
                    )}
                    onClick={item.onClick}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
          
          {/* Auth Section */}
          <div className="hidden md:flex md:items-center">
            <ThemeToggle className="mr-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" />
            {user ? (
              <div className="flex items-center space-x-4">
                <TrackingLink href={dashboardHref} campaign="navbar" content="dashboard">
                  <Button variant="default" className={cn(
                    siteName === 'askjds'
                      ? "bg-jdorange hover:bg-jdorange-dark"
                      : "bg-jdblue hover:bg-jdblue-light"
                  )}>
                    {dashboardText}
                  </Button>
                </TrackingLink>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-full w-10 h-10 p-0 ml-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white">
                      <User className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>
                      {user?.email}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <TrackingLink href="/settings" campaign="navbar" content="settings">
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Settings</span>
                      </TrackingLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Sign out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <TrackingLink href="/login" campaign="navbar" content="login">
                  <Button variant="ghost">Log In</Button>
                </TrackingLink>
                <TrackingLink href="/auth?tab=signup" campaign="navbar" content="sign_up">
                  <Button variant="default" className={cn(
                    siteName === 'askjds'
                      ? "bg-jdorange hover:bg-jdorange-dark"
                      : "bg-jdblue hover:bg-jdblue-light"
                  )}>
                    Sign Up
                  </Button>
                </TrackingLink>
              </div>
            )}
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 dark:text-gray-300 hover:text-jdblue"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden">
          <div className={cn(
            "px-2 pt-2 pb-3 space-y-1 border-b dark:border-gray-700",
            "bg-white dark:bg-gray-900"
          )}>
            {navItems.map((item) => {
              const isActiveItem = item.isActive || location.pathname === item.href;
              const isExternal = item.href.startsWith('http');

              // If mobileChildren is provided, render as expandable section (for Features)
              if (item.mobileChildren) {
                return (
                  <div key={item.label} className="space-y-1">
                    <div className={cn(
                      "flex items-center px-3 py-2 rounded-md text-base font-medium",
                      isActiveItem && siteName === 'askjds' && "text-[#F37022]",
                      isActiveItem && siteName !== 'askjds' && "text-jdblue",
                      !isActiveItem && "text-gray-600 dark:text-gray-400"
                    )}>
                      {item.label}
                    </div>
                    {/* Render mobile children as flat list */}
                    <div className="pl-6 space-y-1">
                      {item.mobileChildren.map((child: any) => (
                        <a
                          key={child.label}
                          href={child.href}
                          className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-[#F37022]"
                          onClick={(e) => {
                            e.preventDefault();
                            const hash = child.href.split('#')[1];
                            const element = document.getElementById(hash);
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth' });
                            }
                            // Close mobile menu after navigation
                            setIsOpen(false);
                          }}
                        >
                          {child.icon}
                          <span>{child.label}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              }

              // If custom element is provided for mobile, render it
              if (item.customElement) {
                return <div key={item.label} className="px-3 py-2">{item.customElement}</div>;
              }

              // External links use <a> tag
              if (isExternal) {
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    className={cn(
                      "flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800",
                      isActiveItem && siteName === 'askjds' && "text-[#F37022] hover:text-[#F37022]",
                      isActiveItem && siteName !== 'askjds' && "text-jdblue hover:text-jdblue",
                      isActiveItem && "bg-gray-50 dark:bg-gray-800"
                    )}
                    onClick={(e) => {
                      if (item.onClick) item.onClick(e);
                      setIsOpen(false);
                    }}
                  >
                    {item.icon && <span className="mr-2">{item.icon}</span>}
                    {item.label}
                  </a>
                );
              }

              // Internal links use React Router Link
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800",
                    isActiveItem && siteName === 'askjds' && "text-[#F37022] hover:text-[#F37022]",
                    isActiveItem && siteName !== 'askjds' && "text-jdblue hover:text-jdblue",
                    isActiveItem && "bg-gray-50 dark:bg-gray-800"
                  )}
                  onClick={(e) => {
                    if (item.onClick) item.onClick(e);
                    setIsOpen(false);
                  }}
                >
                  {item.icon && <span className="mr-2">{item.icon}</span>}
                  {item.label}
                </Link>
              );
            })}
            
            {/* Theme Toggle */}
            <div className="px-3 py-2">
              <ThemeToggle variant="switch" />
            </div>

            {/* Mobile Auth Section */}
            {user ? (
              <>
                <TrackingLink
                  href={dashboardHref}
                  campaign="navbar"
                  content="mobile_dashboard"
                  className={cn(
                    "flex items-center px-3 py-2 rounded-md text-base font-medium text-white hover:bg-opacity-90",
                    siteName === 'askjds'
                      ? "bg-jdorange hover:bg-jdorange/90"
                      : "bg-jdblue hover:bg-jdblue/90"
                  )}
                >
                  {dashboardText}
                </TrackingLink>
                <TrackingLink
                  href="/settings"
                  campaign="navbar"
                  content="mobile_settings"
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-400 hover:text-jdblue hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </TrackingLink>
                <button
                  onClick={handleSignOut}
                  className="flex w-full items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-400 hover:text-jdblue hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </button>
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-500">
                  Signed in as: {user.email}
                </div>
              </>
            ) : (
              <>
                <TrackingLink
                  href="/login"
                  campaign="navbar"
                  content="mobile_login"
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-400 hover:text-jdblue hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Log In
                </TrackingLink>
                <TrackingLink
                  href="/auth?tab=signup"
                  campaign="navbar"
                  content="mobile_sign_up"
                  className={cn(
                    "flex items-center px-3 py-2 rounded-md text-base font-medium text-white",
                    siteName === 'askjds'
                      ? "bg-jdorange hover:bg-jdorange/90"
                      : "bg-jdblue hover:bg-jdblue/90"
                  )}
                >
                  Sign Up
                </TrackingLink>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
} 